import { useState } from 'react';
import { useAtom } from 'jotai';
import firebase from 'firebase';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { db } from '../../../../shared/services/firebase';
// constants
import { useToast } from '../../../../shared/hooks';
import {studentSupportTicketsAtom} from '../../../../shared/state/atoms'
import { firestoreKeys } from '../../../../shared/utils/constants';

export default function useSupportTicketList() {
  const [isSendingSupport, setIsSendingSupport] = useState(false);
  const [isLoadingSupport, setIsLoadingSupport] = useState(false);
  const [studentSupportTickets, setStudentSupportTicketsAtom] = useAtom(studentSupportTicketsAtom);
  const { showError, showSuccess } = useToast();
  const [supportTicketList] = useCollectionData(firebase.firestore().collection(firestoreKeys.SUPPORT_TICKETS), {
    idField: 'id',
  });

  const getAdminSupportTickets = async () => {
    setIsLoadingSupport(true);

    try {
      const supportTicketsSnapshot = await db.collection('SupportTickets').get();

      if (supportTicketsSnapshot.empty) {
        console.log('No support tickets found.');
        return [];
      }

      let allMessages = [];

      await Promise.all(
        supportTicketsSnapshot.docs.map(async (ticketDoc) => {
          try {
            const ticketData = ticketDoc.data();
            const studentRef = ticketData?.StudentReference;

            let studentData = null;
            if (studentRef) {
              const studentDoc = await studentRef.get();
              if (studentDoc.exists) {
                studentData = studentDoc.data();
              }
            }

            // Fetch messages from the "Tickets" subcollection
            const messagesSnapshot = await ticketDoc.ref
              .collection('Tickets')
              .get();
            const messages = messagesSnapshot.docs.map((msgDoc) => ({
              id: msgDoc.id,
              supportTicketId: ticketDoc.id, // Link back to the ticket
              student: studentData, // Include student details in each message
              ...msgDoc.data(),
            }));

            // Add all messages to the flattened array
            allMessages = allMessages.concat(messages);
            setIsLoadingSupport(false);
          } catch (error) {
            console.error('Error fetching support ticket:', error);
            setIsLoadingSupport(false);
            showError('Error getting support tickets: ', error);
          }
        })
      );

      allMessages.sort((a, b) => {
        // Sort by isResolved (false first, true last)
        if (a.isResolved !== b.isResolved) {
          return a.isResolved - b.isResolved;
        }
        // If isResolved is the same, sort by CreatedDate (newest first)
        return a.Meta.CreatedDate.toDate() - b.Meta.CreatedDate.toDate();
      });

      return allMessages;
    } catch (error) {
      console.error('Error getting admin support tickets:', error);
      return [];
    }
  };

  const getStudentSupportTickets = async (studentId) => {
    setIsLoadingSupport(true);
    try {
      const ticketsRef = await db.collection('SupportTickets').where('Id', '==', studentId).get();

      let docId;

      if (!ticketsRef.empty) {
        docId = ticketsRef.docs[0].id;
      } else {
        setIsLoadingSupport(false);
        return;
      }

      const ticketsList = await db
        .collection('SupportTickets')
        .doc(docId)
        .collection('Tickets')
        .orderBy('Meta.CreatedDate')
        .get();

      const messages = ticketsList.docs.map((doc) => ({
        Id: doc.id,
        ...doc.data(),
      }));

      setIsLoadingSupport(false);
      return messages;
    } catch (error) {
      console.error('Error fetching support tickets:', error);
      setIsLoadingSupport(false);

      showError('Error getting support tickets. Please try again, or contact Admin');
      setIsLoadingSupport(false);
      return [];
    }
  };
  const sendSupportTicket = async (studentId, subject, message) => {
    setIsSendingSupport(true);
    try {
      const ticketsRef = await db.collection('SupportTickets').where('Id', '==', studentId).get();

      let docId;

      if (!ticketsRef.empty) {
        docId = ticketsRef.docs[0].id;
      } else {
        const studentRef = db.collection('Students').doc(studentId);
        const newTicketRef = await db.collection('SupportTickets').add({ Id: studentId, StudentReference: studentRef });
        docId = newTicketRef.id;
      }

      const ticketData = {
        Message: message,
        Subject: subject,
        isResolved: false,
        Meta: {
          CreatedDate: firebase.firestore.Timestamp.now(),
          ModifiedDate: firebase.firestore.Timestamp.now(),
        },
      };

      await db.collection('SupportTickets').doc(docId).collection('Tickets').add(ticketData);
      showSuccess('Support ticket successfully sent');
    } catch (error) {
      console.error('Error adding support tickets:', error);
      showError('Error sending support ticket. Please try again, or contact Admin');

      return [];
    }
    setIsSendingSupport(false);
  };

  const updateMessageResolved = async (ticketId, messageId) => {
    setIsSendingSupport(true);

    try {
      const messageRef = db.collection('SupportTickets').doc(ticketId).collection('Tickets').doc(messageId);

      const messageDoc = await messageRef.get();

      if (messageDoc.exists) {
        const currentStatus = messageDoc.data()?.isResolved ?? false;
        await messageRef.update({ isResolved: !currentStatus });
        setStudentSupportTicketsAtom((prevTickets) =>
          prevTickets.map((ticket) =>
            ticket.Id === ticketId
              ? {
                  ...ticket,
                  Messages: ticket.Messages.map((msg) =>
                    msg.Id === messageId ? { ...msg, isResolved: !currentStatus } : msg
                  ),
                }
              : ticket
          )
        );
        setIsSendingSupport(false);
        showSuccess('Support ticket status successfully updated');
      } else {
        setIsSendingSupport(false);
        showError(`Error updating support ticket ${messageId} status`);
      }
    } catch (error) {
      console.error(`Error Resolving/Unresolving ticket ${messageId}:`, error);
    }
  };

  return {
    isLoadingSupport,
    isSendingSupport,
    supportTicketList,
    getStudentSupportTickets,
    getAdminSupportTickets,
    sendSupportTicket,
    updateMessageResolved,
  };
}
